import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext'; // Import useSocket
import { getUnreadNotificationsCount, getUnreadPulseHubNotificationsCount } from '../services/notificationApi';
import { useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

// Create audio instance outside the component to prevent re-creation on each render
const notificationSound = new Audio('/sounds/bell.mp3');

const NotificationCountContext = createContext();

export const useNotificationCount = () => {
  return useContext(NotificationCountContext);
};

export const NotificationCountProvider = ({ children }) => { // Removed socket prop
  const { authState } = useAuth();
  const socket = useSocket(); // Get socket from context
  const user = authState?.user;
  const [unreadCount, setUnreadCount] = useState(0);
  const [pulseHubUnreadCount, setPulseHubUnreadCount] = useState(0);
  const isMounted = useRef(true); // Use ref to track mount status
  const toast = useToast();
  const navigate = useNavigate();

  // Function to play notification sound
  const playBellSound = useCallback(() => {
    //console.log('NotificationCountContext: Attempting to play bell sound.');
    // Check if audio is ready to play
    if (notificationSound.readyState >= 2) {
      // Audio is loaded and can be played
      notificationSound.currentTime = 0; // Reset to start
      notificationSound.play()
        .then(() => console.log('NotificationCountContext: Bell sound played successfully.'))
        .catch(error => console.error("NotificationCountContext: Error playing sound:", error));
    } else {
      // Audio might not be loaded yet, set the source and try again
      notificationSound.src = '/sounds/bell.mp3';
      notificationSound.load();
      notificationSound.oncanplaythrough = () => {
        notificationSound.play()
          .then(() => console.log('NotificationCountContext: Bell sound played successfully after loading.'))
          .catch(error => console.error("NotificationCountContext: Error playing sound after loading:", error));
        notificationSound.oncanplaythrough = null; // Clean up event listener
      };
      notificationSound.onerror = (e) => {
        console.error("NotificationCountContext: Could not load sound file:", e);
      };
    }
  }, []); // Empty dependency array means this function is memoized and won't change on re-renders

  // Fetch initial unread counts
  useEffect(() => {
    //console.log('[NOTIFICATION-CONTEXT] 🔄 useEffect triggered at:', new Date().toISOString(), 'user:', user ? user.id : 'null');
    const fetchUnreadCounts = async () => {
      try {
        if (user) {
          //console.log('[NOTIFICATION-CONTEXT] 📡 Fetching notification counts...');
          // Fetch marketplace notifications count
          const marketplaceCount = await getUnreadNotificationsCount();
          setUnreadCount(marketplaceCount);
          
          // Fetch Pulse Hub notifications count
          const pulseHubCount = await getUnreadPulseHubNotificationsCount();
          setPulseHubUnreadCount(pulseHubCount);
          
          //console.log('[NOTIFICATION-CONTEXT] ✅ Received counts - Marketplace:', marketplaceCount, 'Pulse Hub:', pulseHubCount);
        } else {
          //console.log('[NOTIFICATION-CONTEXT] ⚠️ No user, skipping notification count fetch');
        }
      } catch (error) {
        console.error('[NOTIFICATION-CONTEXT] ❌ Error fetching initial notification counts:', error);
      }
    };

    fetchUnreadCounts();

    // Cleanup for mount status
    return () => {
      isMounted.current = false;
    };
  }, [user]); // Depend on user

  // Handle real-time updates via Socket.IO
  useEffect(() => {
    //console.log('NotificationCountContext: useEffect for listener triggered. authState:', authState, 'Socket:', socket);

    if (!socket || !authState?.user) {
      //console.log('NotificationCountContext: Socket not available or user not authenticated, skipping listener setup.');
      return;
    }

    const handleNewNotification = (notification) => {
      //console.log('NotificationCountContext: newNotification event received!', notification); // Log event reception

      // Play notification sound when a new notification arrives
      playBellSound();
      //console.log('NotificationCountContext: Attempting to play bell sound.'); // Log sound attempt

      // Show toast notification based on notification type and source
      const isPulseHub = notification.source === 'pulse_hub';
      const isComment = notification.type === 'post_comment' || notification.type === 'comment_reply';
      const isLike = notification.type === 'like';
      const isTip = notification.type === 'tip';
      const isReport = notification.type === 'report';

      if (isComment || isLike || isTip || isReport) {
        let toastTitle, toastIcon, description;

        if (isComment) {
          const isPostComment = notification.type === 'post_comment';
          toastTitle = isPostComment ? 'New Comment' : 'New Reply';
          toastIcon = isPostComment ? '💬' : '↩️';
          
          // Extract commenter name from message for better formatting
          const commenterName = notification.message.split(' ')[0];
          const commentPreview = notification.message.substring(notification.message.indexOf('"') + 1, notification.message.lastIndexOf('"'));
          
          description = `${commenterName} ${isPostComment ? 'commented on your post' : 'replied to your comment'}: "${commentPreview}"`;
        } else if (isLike) {
          const likerName = notification.message.split(' ')[0];
          toastTitle = 'New Like';
          toastIcon = '❤️';
          description = `${likerName} liked your ${notification.content_type}`;
        } else if (isTip) {
          const tipperName = notification.message.split(' ')[0];
          toastTitle = 'New Tip';
          toastIcon = '💰';
          description = `${tipperName} tipped your ${notification.content_type}`;
        } else if (isReport) {
          const reporterName = notification.message.split(' ')[0];
          toastTitle = 'New Report';
          toastIcon = '⚠️';
          description = `${reporterName} reported your ${notification.content_type}`;
        }

        toast({
          title: `${toastIcon} ${toastTitle}`,
          description,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
          variant: 'left-accent',
          containerStyle: {
            bg: 'white',
            borderLeft: `4px solid #4CAF50`,
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            backdropFilter: 'blur(10px)',
          },
          onCloseComplete: () => {
            // Navigate based on notification source
            if (isPulseHub) {
              // Navigate to Pulse Hub notifications page
              navigate('/hub/notifications');
            } else if (notification.related_entity_id) {
              // Navigate to notifications when toast is clicked
              navigate('/notifications');
            }
          },
        });
      }

      // Update the appropriate unread count based on source
      //console.log('NotificationCountContext: Attempting to update state.'); // Log attempt
      if (isPulseHub) {
        setPulseHubUnreadCount(prevCount => prevCount + 1);
      } else {
        setUnreadCount(prevCount => prevCount + 1);
      }
    };

    // Handle notification read events
    const handleNotificationRead = ({ notificationId }) => {
      //console.log('NotificationCountContext: notificationRead event received:', notificationId);
      // This will be handled by the API calls that mark notifications as read
    };

    // Handle all notifications read events
    const handleAllNotificationsRead = ({ userId }) => {
      //console.log('NotificationCountContext: allNotificationsRead event received for user:', userId);
      if (userId === authState.user.id) {
        setUnreadCount(0);
        setPulseHubUnreadCount(0);
      }
    };

    // Attach the listeners
    //console.log('NotificationCountContext: Attaching notification listeners.');
    socket.on('newNotification', handleNewNotification);
    socket.on('notificationRead', handleNotificationRead);
    socket.on('allNotificationsRead', handleAllNotificationsRead);

    // Cleanup function for the effect
    return () => {
      //console.log('NotificationCountContext: useEffect cleanup triggered.');
      // Remove the listeners
      if (socket) {
        //console.log('NotificationCountContext: Removing notification listeners.');
        socket.off('newNotification', handleNewNotification);
        socket.off('notificationRead', handleNotificationRead);
        socket.off('allNotificationsRead', handleAllNotificationsRead);
      }
    };
  }, [socket, authState, playBellSound, toast, navigate]); // Depend on socket, authState, playBellSound, toast, and navigate

  // Function to manually update the count (e.g., when marking as read on the frontend)
  const updateUnreadCount = (newCount) => {
    if (isMounted.current) { // Only update state if component is mounted
      setUnreadCount(newCount);
    }
  };

  // Function to manually update the Pulse Hub count (e.g., when marking as read on the frontend)
  const updatePulseHubUnreadCount = (newCount) => {
    if (isMounted.current) { // Only update state if component is mounted
      setPulseHubUnreadCount(newCount);
    }
  };

  const value = {
    unreadCount,
    pulseHubUnreadCount,
    setUnreadCount, // Expose setUnreadCount for direct updates if needed
    updateUnreadCount, // Expose a dedicated update function
    updatePulseHubUnreadCount, // Expose Pulse Hub update function
    playBellSound, // Expose the sound function for manual triggering if needed
  };

  return (
    <NotificationCountContext.Provider value={value}>
      {children}
    </NotificationCountContext.Provider>
  );
};
